import React, { useEffect, useState } from 'react';
import { Button, Box, Text, Icon } from '@adminjs/design-system';
import ComplaintStatusModal from './ComplaintStatusModal.js';
import ComplaintFormPage from './ComplaintFormPage.js';
import AVRequestFormPage from './AVRequestFormPage.js';
import Noticeboard from './Noticeboard.js';
import { useHashRoute } from './useHashRoute.js';

type User = {
  id: number;
  displayName: string;
  designation: string;
  avatarUrl?: string;
};

function App() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const { route, navigate } = useHashRoute();

  useEffect(() => {
    fetch('/api/users')
      .then(res => res.json())
      .then(setUsers)
      .catch((er) => {
        console.error('Error fetching users:', er);
      })
      .finally(() => setLoading(false));
  }, []);

  // SPA page tracking for status modal (hash routes are tracked automatically via URL)
  useEffect(() => {
    if (showStatusModal) {
      window.history.replaceState({}, '', '/?modal=status');
    }
  }, [showStatusModal]);

  // Route-based rendering for full-page forms
  if (route === '/complaint') {
    return <ComplaintFormPage onBack={() => navigate('/')} />;
  }
  if (route === '/av-request') {
    return <AVRequestFormPage onBack={() => navigate('/')} />;
  }

  // Homepage
  return (
    <Box style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {showStatusModal && <ComplaintStatusModal setShowStatusModal={setShowStatusModal} />}

      {/* Hero Section */}
      <Box
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          borderBottom: '4px solid #dc2626',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Box
          p="xxl"
          style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          <Box display="flex" justifyContent="left" alignItems="center" flexWrap="wrap" style={{ gap: '20px' }}>
            <img src="/logo2.svg" alt="Infocell" style={{ width: '100px', height: '100px', alignSelf: 'center', verticalAlign: 'middle' }} />
            <Box>
              <Text fontSize="h1" fontWeight="bold" mb="sm" style={{ fontSize: '2.5rem', color: 'white' }}>
                Infocell
              </Text>
              <Text fontSize="lg" style={{ maxWidth: '600px', color: 'rgba(255,255,255,0.9)' }}>
                Information Technology Services - IT support hub for technical assistance and equipment requests.
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box p="xxl" style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Quick Actions Section */}
        <Box mb="xxl">
          <Box
            display="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}
          >
            {/* Complaint Card */}
            <Box
              bg="white"
              p="xl"
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onClick={() => navigate('/complaint')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mb="default"
                style={{
                  width: '64px',
                  height: '64px',
                  background: '#dc2626',
                  borderRadius: '50%',
                  margin: '0 auto 16px'
                }}
              >
                <Icon icon="AlertOctagon" color="white" style={{ fontSize: '32px' }} />
              </Box>
              <Text fontSize="h4" fontWeight="bold" mb="sm" color="grey100">
                Submit Complaint
              </Text>
              <Text color="grey80" fontSize="default">
                Report technical issues, maintenance requests, or any IT-related problems
              </Text>
              <Box mt="lg">
                <Button variant="primary" size="lg" style={{ width: '100%' }}>
                  Get Started
                </Button>
              </Box>
            </Box>

            {/* Status Check Card */}
            <Box
              bg="white"
              p="xl"
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onClick={() => setShowStatusModal(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mb="default"
                style={{
                  width: '64px',
                  height: '64px',
                  background: '#1e40af',
                  borderRadius: '50%',
                  margin: '0 auto 16px'
                }}
              >
                <Icon icon="Search" color="white" style={{ fontSize: '32px' }} />
              </Box>
              <Text fontSize="h4" fontWeight="bold" mb="sm" color="grey100">
                Track Complaint Status
              </Text>
              <Text color="grey80" fontSize="default">
                Check the status of your submitted complaints
              </Text>
              <Box mt="lg">
                <Button variant="primary" size="lg" style={{ width: '100%' }}>
                  Check Status
                </Button>
              </Box>
            </Box>


            {/* AV Request Card */}
            <Box
              bg="white"
              p="xl"
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onClick={() => navigate('/av-request')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mb="default"
                style={{
                  width: '64px',
                  height: '64px',
                  background: '#059669',
                  borderRadius: '50%',
                  margin: '0 auto 16px'
                }}
              >
                <Icon icon="Video" color="white" style={{ fontSize: '32px' }} />
              </Box>
              <Text fontSize="h4" fontWeight="bold" mb="sm" color="grey100">
                Request AV Equipment
              </Text>
              <Text color="grey80" fontSize="default">
                Book audio-visual equipment for events, presentations, and meetings
              </Text>
              <Box mt="lg">
                <Button variant="info" size="lg" style={{ width: '100%' }}>
                  Book Now
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Notice Board Section */}
        <Box mb="xxl">
          <Noticeboard />
        </Box>

        {/* Staff Directory Section */}
        <Box>
          <Text fontSize="h3" fontWeight="bold" style={{ color: 'rgba(54, 52, 52, 0.9)', marginBottom: '20px' }} textAlign="center">
            Our Team
          </Text>

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p="xxl"
              bg="white"
              style={{ borderRadius: '12px' }}
            >
              <Text color="grey80">Loading team members...</Text>
            </Box>
          ) : users.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p="xxl"
              bg="white"
              style={{ borderRadius: '12px' }}
            >
              <Text color="grey80">No staff members available</Text>
            </Box>
          ) : (
            <Box
              display="grid"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '24px'
              }}
            >
              {users.map(user => (
                <Box
                  key={user.id}
                  bg="white"
                  p="lg"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    mb="default"
                  >
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.displayName}
                        style={{
                          width: '96px',
                          height: '96px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '4px solid #f0f0f0'
                        }}
                      />
                    ) : (
                      <Box
                        style={{
                          width: '96px',
                          height: '96px',
                          borderRadius: '50%',
                          background: '#1e40af',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '40px',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      >
                        {user.displayName.charAt(0).toUpperCase()}
                      </Box>
                    )}
                  </Box>
                  <Text fontSize="h4" fontWeight="bold" color="grey100" mb="xs">
                    {user.displayName}
                  </Text>
                  <Text fontSize="default" color="grey80">
                    {user.designation}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box mt="xxl" pt="xl" style={{ borderTop: '1px solid #e5e7eb' }}>
          <Text color="grey80" textAlign="center" fontSize="default">
            © {new Date().getFullYear()} Infocell. All rights reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

export default App;
